import { useFsCategoryStore } from '@/store/audit/fsCategoryStore';
import {
    applyB61AlLastYearSplit,
    b6Row6Sign,
    buildGlTotalByFsCode,
    computeB61LfpLastYear,
} from '@/utils/glToFsMapper';
import {
    resolveBsInventoriesFromBsTree,
    shouldHidePlCostOfSalesRow,
} from '@/utils/plCostOfSalesVisibility';
import { buildExcludedBankSummaryKeysFromDataKeys } from '@/components/account/gl-invoice/mode/tabs/bankColumnUtils';

type ColumnHint = { fsCode?: string | null; glCode?: string | null };
type ByCode = Record<string, { fsId?: number } | undefined>;

const COT_EPS = 0.01;

function parseFsAmount(raw: string | undefined): number {
    if (raw == null || String(raw).trim() === '') return 0;
    const n = Number(String(raw).replace(/,/g, ''));
    return Number.isFinite(n) ? n : 0;
}

/**
 * 无存货时 UI 隐藏 Cost of Sales（与 Purchases 等价）。
 * Excel 常把上年采购额写在 PL-COT-COS（标题却是 Purchase）；若只留在 COS，
 * 可见的 Purchases 行会是空的，且本年 COS 偏好会在 harvest 时把金额清掉。
 * 此时把 COS → PUR，让可见行与 LY COT=PUR 推断一致。
 */
export function relocateHiddenCosLastYearToPur(
    pl: Record<number, string>,
    byCode: ByCode,
    plTree?: any[] | null,
    bsTree?: any[] | null,
    bsLastYear?: Record<number, string>,
): Record<number, string> {
    const cosId = byCode['PL-COT-COS']?.fsId;
    const purId = byCode['PL-COT-PUR']?.fsId;
    if (!cosId || !purId) return pl;

    const invId = byCode['BS-CA-INV']?.fsId;
    const fromTree = resolveBsInventoriesFromBsTree(bsTree);
    const bsInvLy = invId != null ? parseFsAmount(bsLastYear?.[invId]) : 0;
    const bsInv = {
        clientCurrent: fromTree.clientCurrent,
        auditedCurrent: fromTree.auditedCurrent,
        lastYearAudited: Math.abs(bsInvLy) >= COT_EPS ? bsInvLy : fromTree.lastYearAudited,
    };
    if (!shouldHidePlCostOfSalesRow(bsInv, plTree)) return pl;

    const cos = parseFsAmount(pl[cosId]);
    const pur = parseFsAmount(pl[purId]);
    if (Math.abs(cos) < COT_EPS || Math.abs(pur) >= COT_EPS) return pl;
    return { ...pl, [purId]: String(cos), [cosId]: '0' };
}

/**
 * 把 GL 上年原值（贷方为负）转成 FS Last Year 展示额。
 * 与 FS Summary（buildFsDisplayValuesFromGl）一致：B6.1 拆分 AL / LFP。
 */
export function priorToFsLastYearMaps(
    summaryPriorYear: Record<string, number>,
    columnDefs: ColumnHint[],
    byCode: ByCode,
    opts?: { plTree?: any[] | null; bsTree?: any[] | null },
): { bs: Record<number, string>; pl: Record<number, string>; lfpLastYear: number } {
    const rawByFs = buildGlTotalByFsCode(columnDefs as any, summaryPriorYear);
    // 先按 B6 符号得到展示额，再对 AL 做 B6.1 拆分（与 FsDrawer 同源）
    const priorDisplayByFs: Record<string, number> = {};
    for (const [fsCode, raw] of Object.entries(rawByFs)) {
        const sign = b6Row6Sign(fsCode);
        if (sign === 0 || !raw) continue;
        priorDisplayByFs[fsCode] = sign * raw;
    }
    const lfpLastYear = applyB61AlLastYearSplit(priorDisplayByFs, rawByFs);
    if (Math.abs(lfpLastYear) >= COT_EPS || priorDisplayByFs['BS-EQ-AL'] != null) {
        priorDisplayByFs['BS-EQ-LFP'] = lfpLastYear;
    }

    const bs: Record<number, string> = {};
    let pl: Record<number, string> = {};
    for (const [fsCode, display] of Object.entries(priorDisplayByFs)) {
        // LFP 由 plAfterTaxLastYear / BalanceSheet 链接行展示，不写入 bs map，避免 harvest 落成 BS-EQ-LFP|LY
        if (fsCode === 'BS-EQ-LFP') continue;
        const fsId = byCode[fsCode]?.fsId;
        if (!fsId) continue;
        const s = String(display);
        if (fsCode.startsWith('PL-')) pl[fsId] = s;
        else bs[fsId] = s;
    }
    pl = relocateHiddenCosLastYearToPur(pl, byCode, opts?.plTree, opts?.bsTree, bs);
    return { bs, pl, lfpLastYear };
}

/** 把 FS Last Year 展示额转回 GL 上年原值。 */
export function fsLastYearMapsToPrior(
    bs: Record<number, string>,
    pl: Record<number, string>,
    byCode: ByCode,
    opts?: { plAfterTaxLastYear?: number | null },
): Record<string, number> {
    const idToCode = new Map<number, string>();
    for (const [code, line] of Object.entries(byCode)) {
        if (line?.fsId) idToCode.set(line.fsId, code);
    }
    const out: Record<string, number> = {};
    const write = (map: Record<number, string>) => {
        for (const [id, raw] of Object.entries(map)) {
            const code = idToCode.get(Number(id));
            if (!code || code === 'BS-EQ-LFP') continue;
            const sign = b6Row6Sign(code);
            if (sign === 0) continue;
            const n = Number(String(raw).replace(/,/g, ''));
            if (!Number.isFinite(n)) continue;
            // 0 也要写入，否则清空上年数时旧值会留在库里
            out[`${code}|LY`] = Math.abs(n) < 0.0001 ? 0 : sign * n;
        }
    };
    write(bs);
    write(pl);

    // 界面 AL 是 B6.1 拆分后的 Retained；库/Excel BU10 是「AL + LFP」整笔，落库前拼回。
    const alId = byCode['BS-EQ-AL']?.fsId;
    if (alId != null) {
        const alSplitDisplay = parseFsAmount(bs[alId]);
        let lfpDisplay = opts?.plAfterTaxLastYear;
        if (lfpDisplay == null || !Number.isFinite(lfpDisplay)) {
            // 回退：用当前 PL 上年 map 按 B6.1 再算一遍 LFP
            const plRawByFs: Record<string, number> = {};
            for (const [id, raw] of Object.entries(pl)) {
                const code = idToCode.get(Number(id));
                if (!code || !code.startsWith('PL-')) continue;
                const sign = b6Row6Sign(code);
                if (sign === 0) continue;
                const n = parseFsAmount(raw);
                plRawByFs[code] = sign * n; // 展示额 → 原值
            }
            lfpDisplay = computeB61LfpLastYear(plRawByFs);
        }
        const alFullDisplay = alSplitDisplay + (Number(lfpDisplay) || 0);
        const sign = b6Row6Sign('BS-EQ-AL');
        out['BS-EQ-AL|LY'] = Math.abs(alFullDisplay) < 0.0001 ? 0 : sign * alFullDisplay;
    }
    delete out['BS-EQ-LFP|LY'];
    return out;
}

/**
 * 若 harvest 把 COS/PUR 都写成 0，但原文上年仍有成本，则按原文恢复（防本年 COS 偏好清掉上年 PUR/COS）。
 */
export function preservePriorCotIfHarvestWiped(
    harvested: Record<string, number>,
    originalPrior: Record<string, number>,
): Record<string, number> {
    const hCos = Number(harvested['PL-COT-COS|LY']) || 0;
    const hPur = Number(harvested['PL-COT-PUR|LY']) || 0;
    if (Math.abs(hCos) >= COT_EPS || Math.abs(hPur) >= COT_EPS) return harvested;

    const origByFs = buildGlTotalByFsCode([], originalPrior);
    const oCos = Number(origByFs['PL-COT-COS'] ?? originalPrior['PL-COT-COS|LY']) || 0;
    const oPur = Number(origByFs['PL-COT-PUR'] ?? originalPrior['PL-COT-PUR|LY']) || 0;
    if (Math.abs(oPur) >= COT_EPS) {
        return { ...harvested, 'PL-COT-PUR|LY': oPur, 'PL-COT-COS|LY': 0 };
    }
    if (Math.abs(oCos) >= COT_EPS) {
        // 无存货场景与 relocate 一致：恢复到可见的 PUR
        return { ...harvested, 'PL-COT-PUR|LY': oCos, 'PL-COT-COS|LY': 0 };
    }
    return harvested;
}

/**
 * AI 重新 Apply：同一 fsCode 的旧 key（|LY / 旧 gl）先清掉再写入，避免同 FS 多 key 加总翻倍。
 * 未出现在 incoming 中的其它 FS 行保留。
 */
export function mergePriorYearReplaceByFs(
    existing: Record<string, number> | null | undefined,
    incoming: Record<string, number>,
): Record<string, number> {
    const fsCodes = new Set<string>();
    for (const k of Object.keys(incoming || {})) {
        const i = k.indexOf('|');
        if (i > 0) fsCodes.add(k.slice(0, i));
    }
    const out: Record<string, number> = {};
    for (const [k, v] of Object.entries(existing || {})) {
        const i = k.indexOf('|');
        const fs = i > 0 ? k.slice(0, i) : '';
        if (fs && fsCodes.has(fs)) continue;
        out[k] = v;
    }
    for (const [k, v] of Object.entries(incoming || {})) {
        if (!k || !Number.isFinite(Number(v))) continue;
        out[k] = Number(v);
    }
    return out;
}

/**
 * 同步替换 remarks：touched FS 先清；incoming 有文案则写入，空串表示清掉
 * （用户改 FS 后旧科目不再展示 client 名）。
 */
export function mergeRemarksReplaceByFs(
    existing: Record<string, string> | null | undefined,
    remarks: Record<string, string> | null | undefined,
    touchedFsCodes: Iterable<string>,
): Record<string, string> {
    const out: Record<string, string> = { ...(existing || {}) };
    for (const fs of touchedFsCodes) {
        if (fs) delete out[fs];
    }
    for (const [fs, text] of Object.entries(remarks || {})) {
        if (!fs) continue;
        const t = (text || '').trim();
        if (t) out[fs] = t;
        else delete out[fs];
    }
    return out;
}

/**
 * Last Year FS 按 fsCode 合并编辑；落库时保留同 FS 下多 GL 分列（如 Sales / Refund）。
 * harvested 为 fs|LY；若 original 同 FS 已有正式 gl key，则按比例缩放写回各列，避免并成单列。
 *
 * 银行辅助列（原币 CNY / RATE 等）不计入 FS Cash，缩放时必须排除，否则分母被原币撑大，
 * 会把每条 HKD GL 同比缩小（ALFINO 式 −142.58）。辅助列金额原样保留、不参与缩放。
 */
export function preservePriorGlSplit(
    harvested: Record<string, number>,
    originalPrior?: Record<string, number> | null,
): Record<string, number> {
    if (!originalPrior || Object.keys(originalPrior).length === 0) return harvested;

    const excludedBankKeys = buildExcludedBankSummaryKeysFromDataKeys([
        ...Object.keys(originalPrior),
        ...Object.keys(harvested),
    ]);

    const origByFs = new Map<string, { key: string; amount: number }[]>();
    for (const [k, v] of Object.entries(originalPrior)) {
        const i = k.indexOf('|');
        if (i <= 0) continue;
        const fs = k.slice(0, i);
        const gl = k.slice(i + 1);
        if (!gl || gl.toUpperCase() === 'LY') continue;
        const list = origByFs.get(fs) || [];
        list.push({ key: k, amount: Number(v) || 0 });
        origByFs.set(fs, list);
    }
    if (origByFs.size === 0) return harvested;

    const out: Record<string, number> = { ...harvested };
    for (const [fs, parts] of origByFs) {
        const lyKey = `${fs}|LY`;
        if (!(lyKey in out) && !parts.some((p) => p.key in out)) {
            // harvested 未触及该 FS：原样保留分列
            for (const p of parts) out[p.key] = p.amount;
            continue;
        }
        const harvestedAmt = Number(out[lyKey] ?? 0);
        delete out[lyKey];

        const scaleParts = parts.filter((p) => !excludedBankKeys.has(p.key));
        const auxParts = parts.filter((p) => excludedBankKeys.has(p.key));
        for (const p of auxParts) out[p.key] = p.amount;

        if (scaleParts.length === 0) {
            // 没有计入 FS 的 GL 可缩放时，保留 |LY，避免把 FS 总额摊进辅助列
            out[lyKey] = harvestedAmt;
            continue;
        }

        const sum = scaleParts.reduce((s, p) => s + p.amount, 0);
        if (Math.abs(sum) < COT_EPS) {
            out[scaleParts[0].key] = harvestedAmt;
            for (let i = 1; i < scaleParts.length; i++) out[scaleParts[i].key] = 0;
            continue;
        }
        if (Math.abs(harvestedAmt - sum) < COT_EPS) {
            for (const p of scaleParts) out[p.key] = p.amount;
            continue;
        }
        const scale = harvestedAmt / sum;
        let allocated = 0;
        for (let i = 0; i < scaleParts.length; i++) {
            if (i === scaleParts.length - 1) {
                out[scaleParts[i].key] = Math.round((harvestedAmt - allocated) * 100) / 100;
            } else {
                const a = Math.round(scaleParts[i].amount * scale * 100) / 100;
                out[scaleParts[i].key] = a;
                allocated += a;
            }
        }
    }
    return out;
}

/** 保存前直接读当前资产负债表 / 损益表上年输入。 */
export function harvestGlLastYearPrior(
    originalPrior?: Record<string, number>,
): Record<string, number> {
    const store = useFsCategoryStore.getState();
    const harvested = fsLastYearMapsToPrior(
        store.bsLastYearValues,
        store.plLastYearValues,
        store.byCode,
        { plAfterTaxLastYear: store.plAfterTaxLastYear },
    );
    if (!originalPrior || Object.keys(originalPrior).length === 0) return harvested;
    const withCot = preservePriorCotIfHarvestWiped(harvested, originalPrior);
    return preservePriorGlSplit(withCot, originalPrior);
}
